import { useCallback, useState, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import type { ScatterPoint } from '../utils/buildScatterPoints';
import { adjustTooltipToViewport } from '../utils/adjustTooltipToViewport';

interface ScatterHeroMarkerProps {
  point: ScatterPoint;
  /** viewBox 上のサムネ半径（画面上はおおよそ一定 px になるよう親が算出） */
  thumbRadiusVb: number;
  /** viewBox 上のホバー用ヒット半径 */
  hitRadiusVb: number;
}

export function ScatterHeroMarker({ point: p, thumbRadiusVb, hitRadiusVb }: ScatterHeroMarkerProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const [tipPos, setTipPos] = useState<{ left: number; top: number } | null>(null);
  const showImg = Boolean(p.headUrl) && !imgFailed;
  const clipId = `scatter-thumb-${p.heroId}`;
  const labelSize = Math.max(4.5, thumbRadiusVb * 1.05);

  const placeTip = useCallback((e: MouseEvent<SVGCircleElement>) => {
    setTipPos(adjustTooltipToViewport(e.clientX, e.clientY));
  }, []);

  const onEnter = useCallback(
    (e: MouseEvent<SVGCircleElement>) => {
      placeTip(e);
    },
    [placeTip]
  );
  const onMove = useCallback((e: MouseEvent<SVGCircleElement>) => {
    setTipPos((prev) => (prev ? adjustTooltipToViewport(e.clientX, e.clientY) : null));
  }, []);
  const onLeave = useCallback(() => setTipPos(null), []);

  return (
    <>
      <g className="win-rate-pick-scatter__marker">
        {showImg ? (
          <image
            x={p.cx - thumbRadiusVb}
            y={p.cy - thumbRadiusVb}
            width={thumbRadiusVb * 2}
            height={thumbRadiusVb * 2}
            href={p.headUrl!}
            clipPath={`url(#${clipId})`}
            preserveAspectRatio="xMidYMid slice"
            pointerEvents="none"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <>
            <circle
              cx={p.cx}
              cy={p.cy}
              r={thumbRadiusVb}
              className="win-rate-pick-scatter__thumb-fallback"
              pointerEvents="none"
            />
            <text
              x={p.cx}
              y={p.cy}
              className="win-rate-pick-scatter__thumb-initial"
              fontSize={labelSize}
              textAnchor="middle"
              dominantBaseline="central"
              pointerEvents="none"
            >
              {p.heroName.charAt(0)}
            </text>
          </>
        )}
        <circle
          cx={p.cx}
          cy={p.cy}
          r={hitRadiusVb}
          fill="transparent"
          className="win-rate-pick-scatter__marker-hit"
          onMouseEnter={onEnter}
          onMouseMove={onMove}
          onMouseLeave={onLeave}
        />
      </g>
      {tipPos &&
        createPortal(
          <div
            role="tooltip"
            className="hero-tooltip win-rate-pick-scatter__scatter-tooltip"
            style={{
              left: tipPos.left,
              top: tipPos.top,
              pointerEvents: 'none',
            }}
          >
            <div className="tooltip-header">
              <strong>{p.heroName}</strong>
              <span className="position">{p.tier}</span>
            </div>
            <div className="tooltip-stats">
              <div className="stat">
                <span className="stat-label">勝率:</span>
                <span className="stat-value win">{p.winPercent.toFixed(1)}%</span>
              </div>
              <div className="stat">
                <span className="stat-label">バンピック率:</span>
                <span className="stat-value ban">{p.banPickPercent.toFixed(1)}%</span>
              </div>
              <div className="stat tier-score">
                <span className="stat-label">ティアスコア:</span>
                <span className="stat-value highlight">{p.tierScore.toFixed(2)}</span>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
